// One TSIA2 question, laid out the way the real test lays it out: a black bar
// carrying the test name and "Question N of M", then stacked Instructions,
// Passage and Question blocks, then the radio choices, then Submit.
//
// Forward-only by construction. There is no Back button, no question
// navigator, and no review screen, because the real test has none of them --
// which is also why the confirmation dialog matters so much: it is the only
// thing standing between the student and an answer they cannot revisit.
import React, { useEffect, useRef, useState } from 'react';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import 'katex/dist/katex.min.css';

import { TSIA2Calculator } from './Calculator';
import {
    HighlightColor,
    HighlighterGlyph,
    HighlighterMenu,
    TSIA2Annotation,
    measureAgainst,
    overlayFor,
} from './Highlighter';
import { TSIA2Question } from './types';

type Props = {
    testName: string;
    question: TSIA2Question;
    position: number;        // 1-based, within this stage
    total: number;
    selectedIndex: number | null;
    onSelect: (index: number) => void;
    onSubmit: () => void;
    onSaveAndFinishLater: () => void;
    // True while a save or a stage handoff is in flight: Submit and Save and
    // Finish Later are disabled so a second click cannot land on the next
    // module's first question.
    busy?: boolean;
    // The active colour lives in the player, not here: the real test keeps a
    // colour selected across questions until Stop Highlight is chosen.
    highlightColor: HighlightColor | null;
    onHighlightColor: (colour: HighlightColor | null) => void;
    // Highlights are offsets held by the player and saved with the answer, so
    // they survive a resume and can be redrawn in the review.
    annotations: TSIA2Annotation[];
    onAnnotationsChange: (next: TSIA2Annotation[]) => void;
    // The confirm panel takes the Submit button's place at the bottom of the
    // screen rather than floating over it, so the screen owns that slot.
    confirmPanel?: React.ReactNode;
};

// Same delimiters the other players use, so authored content renders
// identically wherever it appears.
const KATEX_OPTIONS = {
    delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '$', right: '$', display: false },
        { left: '\\(', right: '\\)', display: false },
        { left: '\\[', right: '\\]', display: true },
    ],
    trust: true,
};

// A rendered KaTeX construct is one unit for highlighting (see
// global/passageHighlight): a marker never goes inside one.
const isAtomicKatex = (element: HTMLElement) => (
    element.classList.contains('accent')
    || (element.classList.contains('katex') && element.querySelector('.indent1, .indent2, .bullet') === null)
);

// Put `marker` into `root` at flat text offset `offset`, splitting a text
// node where needed. Past the end, it goes last.
function insertAtOffset(root: HTMLElement, offset: number, marker: HTMLElement) {
    let flat = 0;
    const visit = (node: Node): boolean => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node as Text;
            const length = text.data.length;
            if (offset <= flat + length) {
                const local = offset - flat;
                if (local <= 0) text.parentNode?.insertBefore(marker, text);
                else if (local >= length) text.parentNode?.insertBefore(marker, text.nextSibling);
                else text.parentNode?.insertBefore(marker, text.splitText(local));
                return true;
            }
            flat += length;
            return false;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        const element = node as HTMLElement;
        // An earlier marker: no text, nothing to count.
        if (element.classList.contains('tsia2-highlight-remove')) return false;
        if (isAtomicKatex(element)) {
            const length = (element.textContent ?? '').length;
            if (offset <= flat) { element.parentNode?.insertBefore(marker, element); return true; }
            if (offset <= flat + length) { element.parentNode?.insertBefore(marker, element.nextSibling); return true; }
            flat += length;
            return false;
        }
        return Array.from(element.childNodes).some(visit);
    };
    if (!Array.from(root.childNodes).some(visit)) root.appendChild(marker);
}

// The remove control sits in the REAL text, right after the highlighted
// words, so the words after it move over to make room -- exactly as on the
// real screen (reference screenshot 05). It is an empty element drawn by CSS,
// so it adds nothing to the text and the saved character offsets, which
// count text only, are untouched. The overlay copies are generated from this
// same markup, so they carry an invisible twin of each marker and their text
// keeps lining up with the real text. Re-placed from scratch on every change:
// the KaTeX render resets the text when the question changes, and the layout
// counter brings this back afterwards.
function placeRemoveMarkers(
    element: HTMLElement | null, annotations: TSIA2Annotation[], scope: 'passage' | 'stem',
) {
    if (!element) return;
    element.querySelectorAll('.tsia2-highlight-remove').forEach(marker => marker.remove());
    annotations.forEach((annotation, index) => {
        const marker = document.createElement('span');
        marker.className = 'tsia2-highlight-remove';
        marker.setAttribute('role', 'button');
        marker.setAttribute('title', 'Remove highlight');
        marker.setAttribute('aria-label', 'Remove highlight');
        marker.dataset.scope = scope;
        marker.dataset.index = String(index);
        insertAtOffset(element, annotation.endIndex, marker);
    });
}

function HighlightOverlays({
    target, annotations, layout, scope,
}: {
    target: React.RefObject<HTMLDivElement>;
    annotations: TSIA2Annotation[];
    layout: number;
    scope: 'passage' | 'stem';
}) {
    const [overlays, setOverlays] = useState<ReturnType<typeof overlayFor>>([]);

    useEffect(() => {
        // Markers first, so the copies made next include them and line up.
        placeRemoveMarkers(target.current, annotations, scope);
        setOverlays(overlayFor(target.current, annotations));
    }, [target, annotations, layout, scope]);

    return (
        <>
            {overlays.map(overlay => (
                <div
                    key={overlay.key}
                    className={`tsia2-overlay tsia2-overlay-${overlay.color}`}
                    aria-hidden='true'
                    dangerouslySetInnerHTML={{ __html: overlay.html }}
                />
            ))}
        </>
    );
}

function Katex({
    content, className, innerRef,
}: {
    content: string | null;
    className?: string;
    innerRef?: React.RefObject<HTMLDivElement>;
}) {
    const ownRef = useRef<HTMLDivElement>(null);
    const ref = innerRef ?? ownRef;

    useEffect(() => {
        if (!ref.current) return;
        // Authored text, not markup: set it as text so a "<" in a math
        // inequality cannot be read as the start of a tag, and so nothing an
        // author types can inject HTML. KaTeX then renders the $...$ runs.
        ref.current.textContent = content ?? '';
        renderMathInElement(ref.current, KATEX_OPTIONS);
        ref.current.innerHTML = ref.current.innerHTML.replaceAll('&nbsp;', ' ');
        // KaTeX renders each base as one unbreakable run, so a passage or an
        // intro authored as $\text{...}$ would refuse to wrap and scroll the
        // pane sideways. The shared helper marks those bases wrappable; it is
        // what every other player calls after rendering.
        applyKatexWrap(ref.current, { enabled: true });
        // A wrapped KaTeX underline paints its lines on top of each other;
        // flatten it to text (italics kept), as the SAT player does.
        flattenKatexUnderlines(ref.current);
    }, [content, ref]);

    return <div ref={ref} className={className} />;
}

export function TSIA2QuestionScreen({
    testName, question, position, total,
    selectedIndex, onSelect, onSubmit, onSaveAndFinishLater, busy = false,
    highlightColor, onHighlightColor, confirmPanel,
    annotations, onAnnotationsChange,
}: Props) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const passageRef = useRef<HTMLDivElement>(null);
    const stemRef = useRef<HTMLDivElement>(null);
    // Bumped after each render so the overlays are rebuilt once KaTeX has
    // finished laying the text out -- offsets measured before that would be
    // against different markup.
    const [layout, setLayout] = useState(0);

    useEffect(() => { setLayout(value => value + 1); }, [question.questionId]);

    // The calculator is a per-item tool: it is offered, or not, by the item
    // on screen. Close it when the item changes so a pop-up opened on one
    // question does not sit over the next one, and never over an item that
    // does not allow a calculator at all.
    useEffect(() => { setCalculatorOpen(false); }, [question.questionId]);

    // A drag or a double-click inside a highlightable region paints it. Both
    // end in a selection, so one handler covers them. The passage is tried
    // first, then the stem; a selection spanning both matches neither, which
    // is what the real test does with a selection that straddles structure.
    const handlePaint = () => {
        if (!highlightColor) return;
        const measured =
            measureAgainst(passageRef.current, question.passage?.passageId ?? null, highlightColor)
            ?? measureAgainst(stemRef.current, null, highlightColor);
        if (!measured) return;
        onAnnotationsChange([...annotations, measured]);
        window.getSelection()?.removeAllRanges();
        setLayout(value => value + 1);
    };

    const removeAnnotation = (target: TSIA2Annotation) => {
        onAnnotationsChange(annotations.filter(item => item !== target));
        setLayout(value => value + 1);
    };

    const passageAnnotations = annotations.filter(item => item.passageId !== null);
    const stemAnnotations = annotations.filter(item => item.passageId === null);

    // The remove markers live inside the KaTeX-rendered text, outside React,
    // so their clicks are picked up here by delegation and mapped back to the
    // highlight through the scope and index the marker carries.
    const handleRemoveClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const marker = (event.target as Element).closest?.('.tsia2-highlight-remove') as HTMLElement | null;
        if (!marker) return;
        const list = marker.dataset.scope === 'passage' ? passageAnnotations : stemAnnotations;
        const target = list[Number(marker.dataset.index)];
        if (target) removeAnnotation(target);
    };

    return (
        <div className='tsia2-shell'>
            <div className='tsia2-bar'>
                <span className='tsia2-bar-title'>
                    {testName} - Question {position} of {total}
                </span>
                <div className='tsia2-tools'>
                    {/* The calculator icon appears only on items College Board
                        flagged for one, which is why this is driven by the
                        item's own calculatorMode rather than the section. */}
                    {question.calculatorMode && (
                        <button
                            type='button'
                            className={'tsia2-tool' + (calculatorOpen ? ' is-active' : '')}
                            onClick={() => setCalculatorOpen(open => !open)}
                        >
                            <span className='tsia2-tool-glyph'>▦</span>
                            <span className='tsia2-tool-label'>Calculator</span>
                        </button>
                    )}
                    <div className='tsia2-tool-wrap'>
                        <button
                            type='button'
                            className={'tsia2-tool' + (highlightColor ? ' is-active' : '')}
                            // The open menu closes itself on any mousedown outside it,
                            // and this button is outside it. Without this, a click here
                            // closed the menu on mousedown and reopened it on click, so
                            // the button could never close its own menu.
                            onMouseDown={event => event.stopPropagation()}
                            onClick={() => setMenuOpen(open => !open)}
                        >
                            <HighlighterGlyph active={highlightColor} />
                            <span className='tsia2-tool-label'>Highlighter</span>
                        </button>
                        {menuOpen && (
                            <HighlighterMenu
                                active={highlightColor}
                                onChoose={colour => { onHighlightColor(colour); setMenuOpen(false); }}
                                onStop={() => { onHighlightColor(null); setMenuOpen(false); }}
                                onDismiss={() => setMenuOpen(false)}
                            />
                        )}
                    </div>
                </div>
                <button
                    type='button'
                    className='tsia2-save-link'
                    onClick={onSaveAndFinishLater}
                    disabled={busy}
                >
                    Save and Finish Later
                </button>
            </div>

            <div
                className='tsia2-body'
                onMouseUp={handlePaint}
                onDoubleClick={handlePaint}
                onClick={handleRemoveClick}
            >
                {question.instructions && (
                    <>
                        <h2 className='tsia2-heading'>Instructions</h2>
                        <p className='tsia2-instructions'>{question.instructions}</p>
                    </>
                )}

                {question.passage && (
                    <div className='tsia2-block'>
                        <h3 className='tsia2-block-heading'>Passage</h3>
                        {question.passage.introduction && (
                            <Katex
                                content={question.passage.introduction}
                                className='tsia2-passage-intro'
                            />
                        )}
                        <div className='tsia2-highlightable'>
                            <Katex
                                content={question.passage.passage}
                                className='tsia2-passage'
                                innerRef={passageRef}
                            />
                            <HighlightOverlays
                                target={passageRef}
                                annotations={passageAnnotations}
                                layout={layout}
                                scope='passage'
                            />
                        </div>
                    </div>
                )}

                <div className='tsia2-block'>
                    <h3 className='tsia2-block-heading'>Question</h3>
                    <div className='tsia2-highlightable'>
                        <Katex
                            content={question.question}
                            className='tsia2-question'
                            innerRef={stemRef}
                        />
                        <HighlightOverlays
                            target={stemRef}
                            annotations={stemAnnotations}
                            layout={layout}
                            scope='stem'
                        />
                    </div>

                    {question.figures.map(figure => (
                        figure.url && (
                            <img
                                key={figure.figureId}
                                src={figure.url}
                                alt={`Figure for question ${position}`}
                                className='tsia2-figure'
                            />
                        )
                    ))}

                    <ul className='tsia2-choices'>
                        {question.choices.map(choice => (
                            <li key={choice.index}>
                                <label className='tsia2-choice'>
                                    <input
                                        type='radio'
                                        name={`tsia2-${question.questionId}`}
                                        checked={selectedIndex === choice.index}
                                        onChange={() => onSelect(choice.index)}
                                    />
                                    <span className='tsia2-choice-label'>{choice.label}</span>
                                    {choice.figure?.url
                                        ? <img src={choice.figure.url} alt={`Choice ${choice.label}`} className='tsia2-choice-figure' />
                                        : <Katex content={choice.text} className='tsia2-choice-text' />}
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className={'tsia2-footer' + (confirmPanel ? ' is-confirming' : '')}>
                {confirmPanel ?? (
                    <button type='button' className='tsia2-btn-submit' onClick={onSubmit} disabled={busy}>
                        Submit
                    </button>
                )}
            </div>

            {/* Keyed on the item: the pop-up remembers which calculator is
                selected, and that choice must not follow the student from a
                graphing item onto one that allows only the basic one. */}
            {calculatorOpen && question.calculatorMode && (
                <TSIA2Calculator
                    key={question.questionId}
                    mode={question.calculatorMode}
                    onClose={() => setCalculatorOpen(false)}
                />
            )}
        </div>
    );
}

// Shown when Submit is pressed with nothing selected. The real test will not
// let a student move on without choosing, and this is the first of the three
// places that rule is enforced (here, the player's own guard, and the server).
export function TSIA2SelectAnswerModal({ onClose }: { onClose: () => void }) {
    return (
        <div className='tsia2-modal-backdrop'>
            <div className='tsia2-modal' role='alertdialog' aria-modal='true'>
                <p className='tsia2-modal-copy'>Please select an answer.</p>
                <div className='tsia2-modal-actions'>
                    <button type='button' className='tsia2-button tsia2-button-primary' onClick={onClose}>
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}

// The "are you sure" step. NOT a centred modal: the real test drops a
// full-width bordered panel into the bottom of the screen, in place of the
// Submit button, with the actions on its right. Copy, button labels and order
// are taken verbatim from the reference screenshot.
export function TSIA2ConfirmPanel({
    onConfirm, onCancel, busy,
}: { onConfirm: () => void; onCancel: () => void; busy: boolean }) {
    return (
        <div className='tsia2-confirm' role='alertdialog' aria-label='Confirm your selection'>
            <div className='tsia2-confirm-header'>Confirm your selection.</div>
            <div className='tsia2-confirm-body'>
                <div className='tsia2-confirm-copy'>
                    <p>Click Confirm to submit this answer.</p>
                    <p>If you want to change your answer, click Cancel and make another selection.</p>
                </div>
                <div className='tsia2-confirm-actions'>
                    <button
                        type='button'
                        className='tsia2-btn-cancel'
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type='button'
                        className='tsia2-btn-confirm'
                        onClick={onConfirm}
                        disabled={busy}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}

export function TSIA2SaveConfirmModal({
    onConfirm, onCancel,
}: { onConfirm: () => void; onCancel: () => void }) {
    return (
        <div className='tsia2-modal-backdrop'>
            <div className='tsia2-modal' role='alertdialog' aria-modal='true'>
                <p className='tsia2-modal-copy'>
                    Are you sure you want to save and finish later?
                </p>
                <div className='tsia2-modal-actions'>
                    <button type='button' className='tsia2-button tsia2-button-secondary' onClick={onCancel}>
                        No
                    </button>
                    <button type='button' className='tsia2-button tsia2-button-primary' onClick={onConfirm}>
                        Yes
                    </button>
                </div>
            </div>
        </div>
    );
}
